// How this browser shows the app: its language and its theme.
//
// Both belong to the screen in front of someone rather than to the account,
// so each browser keeps its own, and the till and the dashboard keep theirs
// apart even in one browser: a till by the door and the owner's laptop never
// change each other.
//
// The language starts as the restaurant's until this screen chooses one; the
// restaurant's language still decides what receipts print in. The theme
// starts as Follow system, which takes the device's own setting and changes
// with it.
//
// localStorage can refuse in a private window or with site data blocked; a
// choice then lasts until the page reloads, which is all it can do.

import type { Language } from './i18n/messages';

export type Surface = 'till' | 'owner' | 'admin';
export type ThemeChoice = 'light' | 'dark' | 'system';

const inBrowser = typeof window !== 'undefined';

function key(surface: Surface, what: 'theme' | 'language'): string {
	return `bosla-${surface}-${what}`;
}

function read(surface: Surface, what: 'theme' | 'language'): string | null {
	try {
		return inBrowser ? localStorage.getItem(key(surface, what)) : null;
	} catch {
		return null;
	}
}

function write(surface: Surface, what: 'theme' | 'language', value: string): void {
	try {
		localStorage.setItem(key(surface, what), value);
	} catch {
		// Kept for this page only.
	}
}

function themeFrom(stored: string | null): ThemeChoice {
	return stored === 'light' || stored === 'dark' ? stored : 'system';
}

function languageFrom(stored: string | null): Language | null {
	return stored === 'ar' || stored === 'msa' || stored === 'en' ? stored : null;
}

let surface = $state<Surface>('till');
const themes = $state<Record<Surface, ThemeChoice>>({
	till: themeFrom(read('till', 'theme')),
	owner: themeFrom(read('owner', 'theme')),
	// The Bosla admin (UC-20), a site of its own with its own choice.
	admin: themeFrom(read('admin', 'theme')),
});
const languages = $state<Record<Surface, Language | null>>({
	till: languageFrom(read('till', 'language')),
	owner: languageFrom(read('owner', 'language')),
	// English only: the admin never offers a language.
	admin: 'en',
});

// The device's own setting, followed live.
const media = inBrowser ? window.matchMedia('(prefers-color-scheme: dark)') : null;
let systemDark = $state(media?.matches ?? false);
media?.addEventListener('change', (event) => (systemDark = event.matches));

/** Which side of the app this page is: the till, the owner's dashboard, or the Bosla admin. */
export function setSurface(next: Surface): void {
	surface = next;
}

export function themeChoice(): ThemeChoice {
	return themes[surface];
}

export function setThemeChoice(next: ThemeChoice): void {
	themes[surface] = next;
	write(surface, 'theme', next);
}

/** The theme to draw: the choice, or the device's when following it. */
export function resolvedTheme(): 'light' | 'dark' {
	const choice = themes[surface];
	if (choice === 'system') return systemDark ? 'dark' : 'light';
	return choice;
}

/** This screen's own language, or null while it follows the restaurant's. */
export function languageChoice(): Language | null {
	return languages[surface];
}

export function setLanguageChoice(next: Language): void {
	languages[surface] = next;
	write(surface, 'language', next);
}
